import React, {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {Link, useParams} from "react-router-dom";
import AutoComplete from "@mui/material/Autocomplete";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import {Chart} from "chart.js/auto";
import SpeechRecognition, {useSpeechRecognition} from "react-speech-recognition";
import {baseUrl, formatDateTime, ipAddress, userLocation, userName} from "../Univ.js";

const NURSE_NOTE_DELETE_URL = (id) => `${baseUrl}delete-nurse-notes/${id}/`;
const INSULIN_DELETE_URL = (id) => `${baseUrl}delete-insulin/${id}/`;


/* =====================================================================
   STATIC DATA
   ===================================================================== */

const TABS = [
    {id: "case", label: "Case Sheet", icon: "fa-notes-medical"},
    {id: "nurse", label: "Nurse Notes", icon: "fa-user-nurse"},
    {id: "cons", label: "Consultant Notes", icon: "fa-user-doctor"},
    {id: "drug", label: "Drug Chart", icon: "fa-pills"},
    {id: "insulin", label: "Insulin Scale", icon: "fa-syringe"},
    {id: "diet", label: "Diet Chart Summary", icon: "fa-apple-whole"},
    {id: "lab", label: "Lab", icon: "fa-flask-vial"},
    {id: "inv", label: "Radiology", icon: "fa-x-ray"},
    {id: "min", label: "Min. Treat", icon: "fa-kit-medical"},
    {id: "med", label: "Medical Summary", icon: "fa-folder-open"},
    {id: "vitals", label: "Vitals", icon: "fa-heart-pulse"},
];

/* exact option values are kept (some have trailing spaces) so saved data still matches */
const SYMPTOMS = ["Headache", "Fever", "Running Nose", "Sore Throat", "Sneezing", "Cough", "Sputum", "Wheeze", "Dyspnea", "PND", "Pedal Oedema", "Anasarca", "Puffy Face", "Swelling", "Neck Swelling", "Groin ", "Pain", "Itching", "Urticaria", "Weakness", "Vertigo", "Unsteadiness", "Chest Pain", "Burning Micturition", "Frequency Of Urine", "Urgency", "Diarrhea", "Constipation", "Bleeding PR", "Epigastric Pain", "Loin Pain ", "Loin Pain Radiating to Rt Groin", "Loin Pain Radiating to Lt Groin", "LBA", "LBA Radiating to Right Thigh", "LBA Radiating to Left Thigh", "Neck Pain", "Neck Pain Radiating to Right Upper Limb", "Neck Pain Radiating to Left Upper Limb"];

const CASE_NOTES = [
    ["allergy", "Allergies"], ["illness", "Other Illness"], ["complaints", "Complaints"],
    ["diagnosis", "Diagnosis"], ["diet", "Dietician Notes"], ["physio", "Physio Notes"],
];

const D = (pairs) => pairs.map(([name, label]) => ({name, label}));
const DEPARTMENT_FIELDS = {
    CARDIOLOGY: D([["chest_pain_type", "Chest Pain Type (Typical/Atypical)"], ["onset_duration", "Onset & Duration"], ["palpitation", "Palpitation"], ["dyspnea", "Breathlessness (Dyspnea)"], ["syncope", "Syncope"], ["ecg_findings", "ECG Findings"], ["echo_findings", "ECHO Findings"], ["risk_factors", "Risk Factors (DM/HTN/Smoking)"], ["indication", "Indication (Reason for Procedure)"], ["procedure_type", "Procedure Type (Angiogram/Angioplasty)"], ["access_site", "Access Site (Radial/Femoral)"], ["vessel_involved", "Vessel Involved (LAD/RCA/LCX)"], ["lesion_details", "Lesion Details"], ["stent_used", "Stent Used (Type/Size)"], ["balloon_used", "Balloon Used"], ["complications", "Complications"], ["contrast_volume", "Contrast Volume Used (ml)"], ["fluoro_time", "Fluoroscopy Time"], ["hemodynamics", "Hemodynamics (BP/HR)"], ["post_procedure_plan", "Post Procedure Plan"], ["pre_procedure_diagnosis", "Pre-Procedure Diagnosis"], ["post_procedure_diagnosis", "Post-Procedure Diagnosis"], ["closure_device", "Closure Device Used"], ["anticoagulation", "Anticoagulation Given"]]),
    ORTHOPAEDICS: D([["joint_pain_site", "Joint Pain Site"], ["pain_duration", "Pain Duration"], ["swelling", "Swelling"], ["tenderness", "Tenderness"], ["range_of_motion", "Range of Motion"], ["fracture_history", "Fracture History"], ["deformity", "Deformity"], ["implant_history", "Implant / Surgery History"]]),
    PSYCHIATRIS: D([["mood", "Mood"], ["affect", "Affect"], ["thought_content", "Thought Content"], ["hallucination", "Hallucination"], ["sleep_pattern", "Sleep Pattern"], ["appetite", "Appetite"], ["suicidal_ideas", "Suicidal Ideation"], ["insight", "Insight & Judgment"]]),
    DERMATOLOGY: D([["lesion_type", "Lesion Type"], ["site_distribution", "Site / Distribution"], ["itching", "Itching"], ["duration", "Duration"], ["pigmentation", "Pigmentation Changes"], ["infection_signs", "Infection Signs"], ["allergy_history", "Allergy History"]]),
    GASTROENTRO_SURGERY: D([["abdominal_pain_site", "Abdominal Pain Site"], ["pain_character", "Pain Character"], ["vomiting", "Vomiting"], ["bowel_changes", "Bowel Habit Changes"], ["bleeding_pr", "Bleeding PR"], ["weight_loss", "Weight Loss"], ["endoscopy_findings", "Endoscopy Findings"]]),
    UROLOGY: D([["urinary_frequency", "Urinary Frequency"], ["burning_micturition", "Burning Micturition"], ["hematuria", "Hematuria"], ["flank_pain", "Flank Pain"], ["stone_history", "Stone History"], ["prostate_symptoms", "Prostate Symptoms"]]),
    GENERAL_SURGERY: D([["lump_site", "Lump Site"], ["lump_size", "Lump Size"], ["tenderness", "Tenderness"], ["reducibility", "Reducibility (Hernia)"], ["cough_impulse", "Cough Impulse"], ["previous_surgery", "Previous Surgery History"]]),
    ENT: D([["ear_discharge", "Ear Discharge"], ["hearing_loss", "Hearing Loss"], ["nasal_block", "Nasal Block"], ["throat_pain", "Throat Pain"], ["voice_change", "Voice Change"], ["tinnitus", "Tinnitus"]]),
    PLASTIC_SURGERY: D([["scar_type", "Scar Type"], ["burn_degree", "Burn Degree"], ["wound_status", "Wound Status"], ["cosmetic_concern", "Cosmetic Concern"], ["reconstruction_need", "Reconstruction Need"]]),
    PULMONOLOGY: D([["cough_duration", "Cough Duration"], ["sputum", "Sputum"], ["breathlessness", "Breathlessness"], ["wheezing", "Wheezing"], ["smoking_history", "Smoking History"], ["chest_exam", "Chest Examination"]]),
    NEUROLOGY: D([["headache_type", "Headache Type"], ["seizure_history", "Seizure History"], ["weakness", "Weakness"], ["sensory_loss", "Sensory Loss"], ["speech_issue", "Speech Issue"], ["gait", "Gait"]]),
    GYNAEC_OBSTETRIC: D([["menstrual_history", "Menstrual History"], ["obstetric_history", "Obstetric History"], ["pregnancy_status", "Pregnancy Status"], ["pv_bleeding", "PV Bleeding"], ["discharge", "Discharge"], ["contraceptive_use", "Contraceptive Use"]]),
    NEPHROLOGY: D([["edema", "Edema"], ["urine_output", "Urine Output"], ["creatinine_level", "Creatinine Level"], ["dialysis_history", "Dialysis History"], ["electrolyte_imbalance", "Electrolyte Imbalance"]]),
    PAEDIATRIC: D([["fever_duration", "Fever Duration"], ["feeding", "Feeding"], ["growth_development", "Growth & Development"], ["immunization", "Immunization Status"], ["activity", "Activity Level"]]),
};

/* lab / radiology / minor-treatment share one workflow */
const ORDER_CFG = {
    lab: {numberUrl: "get_appt_lab_number/", createUrl: "ip-lab-create/", ids: "ltest_id", names: "lab_test", amounts: "lab_amount", tot: "lab_tot", disc: "lab_disc", status: "lab_status", linkField: "lab_id", emptyTitle: "Select Lab Test", emptyText: "Please select at least one lab test.", done: "Lab entry has been added."},
    inv: {numberUrl: "get_appt_inv_number/", createUrl: "ip-inv-create/", ids: "itest_id", names: "inv_test", amounts: "inv_amount", tot: "inv_tot", disc: "inv_disc", status: "inv_status", linkField: "inv_id", emptyTitle: "Select Radiology Test", emptyText: "Please select at least one radiology test.", done: "Radiology entry has been added."},
    min: {numberUrl: "get_appt_min_number/", createUrl: "ip-min-create/", ids: "min_id", names: "min_test", amounts: "min_amount", tot: "min_tot", disc: "min_disc", status: "min_status", linkField: null, unitsField: "min_units", emptyTitle: "Select Treatment", emptyText: "Please select at least one MIN treatment.", done: "Min. treatment entry has been added."},
};

/* =====================================================================
   HELPERS
   ===================================================================== */

const uid = (() => {
    let n = 0;
    return () => `k${++n}`;
})();

const splitCsv = (v) => String(v ?? "").split(",").map((s) => s.trim());
const joinText = (base, spoken) => (!spoken ? base || "" : (base ? base.replace(/\s+$/, "") + " " : "") + spoken);
const pad = (n) => String(n).padStart(2, "0");
const formatLocalDate = (d) => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "");
const formatNoteTime = (d) => `${formatLocalDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toast = (title, icon = "success") => Swal.fire({title, icon, timer: 2000, showConfirmButton: false});
const confirmBox = (text = "Are you Sure you want to delete this") =>
    Swal.fire({title: "Confirm", text, icon: "info", confirmButtonText: "Continue", showCancelButton: true}).then((r) => r.isConfirmed);

const blankDrug = () => ({key: uid(), id: "", qty: ""});
const blankOrder = (key) => ({key: key || uid(), id: "", name: "", amount: 0, units: 1});

const CASE_KEYS = ["weight", "height", "bmi", "symptom", "allergy", "illness", "complaints", "diagnosis", "diet", "physio"];
const emptyCase = () => Object.fromEntries(CASE_KEYS.map((k) => [k, ""]));

const statusBadge = (key, map) => (row) => {
    const m = map[String(row[key])];
    return m ? <span className={`badge ${m[1]}`}>{m[0]}</span> : <span>unknown status</span>;
};
const labBadge = statusBadge("lab_status", {1: ["pending in cashier", "bg-warning"], 2: ["Sent to Lab", "bg-success"], 3: ["View", "bg-success"]});
const invBadge = statusBadge("inv_status", {1: ["pending in cashier", "bg-warning"], 2: ["Sent to Radiology", "bg-success"], 3: ["View", "bg-success"]});

/* =====================================================================
   SMALL COMPONENTS (outside the page so typing never remounts them)
   ===================================================================== */

function SectionTitle({icon, children, right}) {
    return (
        <div className="cd-section-title">
            <span><i className={`fa-solid ${icon}`}/>{children}</span>
            {right}
        </div>
    );
}

function Empty({children = "Nothing to show yet."}) {
    return <div className="text-center text-muted py-4"><i className="fa-regular fa-folder-open me-2"/>{children}</div>;
}

function NoteField({label, name, rows = 5, value, onChange, active, onStart, onStop, supported, col = "col-xl-4 col-lg-6 col-md-6"}) {
    return (
        <div className={`${col} mb-4`}>
            <div className={`field-card h-100 ${active ? "is-listening" : ""}`}>
                <label className="form-label fw-semibold">
                    <i className="fa-solid fa-file-medical text-success me-2"/>{label}
                    {active && <span className="cd-live">● listening</span>}
                </label>
                <textarea className="form-control no-comma" rows={rows} value={value} onChange={(e) => onChange(name, e.target.value)}/>
                <div className="cd-mic">
                    <button type="button" tabIndex={-1} className="btn btn-outline-danger" disabled={!supported} onClick={onStop}>
                        <i className="fa-solid fa-microphone-lines-slash"/>
                    </button>
                    <button type="button" tabIndex={-1} className="btn btn-outline-success" disabled={!supported} onClick={() => onStart(name)}>
                        <i className="fa-solid fa-microphone-lines"/>
                    </button>
                </div>
            </div>
        </div>
    );
}

function useOrderRows(labelKey) {
    const [rows, setRows] = useState([blankOrder()]);
    const select = (i, val) => setRows((p) => p.map((r, x) => (x !== i ? r : val
        ? {...r, id: val.id, name: val[labelKey] || "", amount: Number(val.amount) || 0, units: r.units || 1}
        : blankOrder(r.key))));
    const setUnits = (i, v) => setRows((p) => p.map((r, x) => (x === i ? {...r, units: v} : r)));
    const add = () => setRows((p) => [...p, blankOrder()]);
    const remove = (i) => setRows((p) => (p.length > 1 ? p.filter((_, x) => x !== i) : [blankOrder()]));
    const reset = () => setRows([blankOrder()]);
    return {rows, select, setUnits, add, remove, reset};
}
const rowTotal = (r, withUnits) => (Number(r.amount) || 0) * (withUnits ? Math.max(parseFloat(r.units) || 0, 0) : 1);
const orderTotal = (st, withUnits) => st.rows.reduce((s, r) => s + (r.id ? rowTotal(r, withUnits) : 0), 0);

function OrderTab({title, nameHeader, items, labelKey, state, units, onSubmit, submitting, viewTitle, viewRows, viewNameKey, badge}) {
    const label = (o) => String(o?.[labelKey] ?? "");
    const total = orderTotal(state, units);
    return (
        <>
            <SectionTitle icon="fa-list-check">{title}</SectionTitle>
            <div className="table-responsive">
                <table className="erp-table-modern cd-table">
                    <thead>
                    <tr>
                        <th style={{width: 70}}>ID</th><th>{nameHeader}</th>
                        {units && <th style={{width: 110}}>Units</th>}
                        <th style={{width: 130}}>Amount</th>
                        {units && <th style={{width: 130}}>G.Total</th>}
                        <th style={{width: 90}}>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    {state.rows.map((row, i) => (
                        <tr key={row.key}>
                            <td><input className="form-control" type="text" value={row.id} readOnly tabIndex={-1}/></td>
                            <td>
                                <AutoComplete
                                    options={items}
                                    value={items.find((it) => String(it?.id) === String(row.id)) || null}
                                    isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                                    getOptionLabel={label}
                                    onChange={(e, val) => state.select(i, val)}
                                    clearOnEscape
                                    handleHomeEndKeys
                                    renderOption={(props, option) => {
                                        const {key, ...rest} = props;
                                        return (
                                            <li key={option?.id ?? key} {...rest}>
                                                <div><strong>{label(option)}</strong>
                                                    {option?.amount !== undefined && <small className="d-block text-muted">₹ {option.amount}</small>}
                                                </div>
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <div ref={params.InputProps.ref}>
                                            <input className="form-control cd-ac-input" type="text" placeholder="Search..." {...params.inputProps}/>
                                        </div>
                                    )}
                                />
                            </td>
                            {units && (
                                <td><input className="form-control" type="number" min="1" value={row.units} onChange={(e) => state.setUnits(i, e.target.value)}/></td>
                            )}
                            <td><input className="form-control" type="number" value={row.amount} readOnly tabIndex={-1}/></td>
                            {units && <td><input className="form-control" type="number" value={row.id ? rowTotal(row, true) : 0} readOnly tabIndex={-1}/></td>}
                            <td><button className="btn btn-outline-danger" type="button" onClick={() => state.remove(i)}><i className="fa-solid fa-trash-can"/></button></td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <div className="cd-order-foot">
                <button className="btn btn-outline-success" type="button" onClick={state.add}><i className="fa-solid fa-plus me-2"/>Add More</button>
                <span className="cd-total">Total <b>₹ {total}</b></span>
                <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={submitting}>
                    <i className="fa-solid fa-paper-plane me-2"/>{submitting ? "Sending..." : "Submit"}
                </button>
            </div>

            {viewRows && (
                <>
                    <SectionTitle icon="fa-clock-rotate-left">{viewTitle}</SectionTitle>
                    <div className="table-responsive">
                        <table className="erp-table-modern cd-table">
                            <thead><tr><th>Appt ID</th><th>Test Name</th><th>Addedby</th><th>Status</th></tr></thead>
                            <tbody>
                            {viewRows.map((v, i) => (
                                <tr key={v.id ?? i}><td>{v.appt_id}</td><td>{v[viewNameKey]}</td><td>{v.addedby}</td><td>{badge(v)}</td></tr>
                            ))}
                            </tbody>
                        </table>
                        {viewRows.length === 0 && <Empty>No requests yet.</Empty>}
                    </div>
                </>
            )}
        </>
    );
}

/* =====================================================================
   PAGE
   ===================================================================== */

function IpConsDetail() {
    const {ip_id} = useParams();

    /* ----- server data ----- */
    const [ipData, setIpData] = useState([]);
    const [consData, setConsData] = useState([]);
    const [conData, setConData] = useState([]);
    const [cnotesData, setCnotesData] = useState([]);
    const [notesData, setNotesData] = useState([]);
    const [drugData, setDrugData] = useState([]);
    const [cdrugData, setCdrugData] = useState([]);
    const [insulinData, setInsulinData] = useState([]);
    const [dietIn, setDietIn] = useState([]);
    const [dietOut, setDietOut] = useState([]);
    const [vitalData, setVitalData] = useState([]);
    const [labview, setLabView] = useState([]);
    const [invview, setInvView] = useState([]);
    const [medview, setMedView] = useState([]);
    const [drugitems, setDrugItems] = useState([]);
    const [labitems, setLabItems] = useState([]);
    const [invitems, setInvItems] = useState([]);
    const [minitems, setMinItems] = useState([]);
    const [drugNames, setDrugNames] = useState({});

    /* ----- editable state ----- */
    const [tab, setTab] = useState("case");
    const [form, setForm] = useState(emptyCase);
    const [icd, setIcd] = useState({icd: "", code: ""});
    const [icdDis, setIcdDis] = useState([]);
    const [icdCode, setIcdCode] = useState([]);
    const [saving, setSaving] = useState(false);

    const [drugRows, setDrugRows] = useState([blankDrug()]);
    const [savingDrug, setSavingDrug] = useState(false);

    const [consSel, setConsSel] = useState("");
    const [consTime, setConsTime] = useState(new Date());
    const [consNotes, setConsNotes] = useState({});
    const [savingCons, setSavingCons] = useState(false);

    const [submittingOrder, setSubmittingOrder] = useState("");
    const lab = useOrderRows("subcat");
    const inv = useOrderRows("components");
    const min = useOrderRows("comp");
    const orders = {lab, inv, min};

    const [medicalDate, setMedicalDate] = useState(new Date());
    const [medFile, setMedFile] = useState({ftype: "ctscan", comments: "", file: null});

    const hydratedRef = useRef(null);
    const triedNamesRef = useRef(new Set());
    const focusRef = useRef(null);
    const baseRef = useRef("");
    const chartRef = useRef(null);

    const ip = ipData[0];
    const patId = ip?.pat?.id;
    const consultId = ip?.primary_consult?.id;

    /* =================================================================
       FETCHING
       ================================================================= */

    const load = useCallback((path, setter) => axios.get(baseUrl + path)
        .then((r) => setter(r.data))
        .catch((e) => console.log(e)), []);

    const loadCon = useCallback(() => load(`view-ip-consultform/${ip_id}`, (d) => setConData(Array.isArray(d) ? d : [])), [load, ip_id]);
    const loadCnotes = useCallback(() => load(`view-consult-note/${ip_id}/`, setCnotesData), [load, ip_id]);
    const loadNurse = useCallback(() => load(`view-nurse-notes/${ip_id}/`, setNotesData), [load, ip_id]);
    const loadDrug = useCallback(() => load(`view-drug-ip/${ip_id}/`, setDrugData), [load, ip_id]);
    const loadInsulin = useCallback(() => load(`view-insulin/${ip_id}/`, setInsulinData), [load, ip_id]);
    const loadDietIn = useCallback(() => load(`view-diet-in/${ip_id}/`, setDietIn), [load, ip_id]);
    const loadDietOut = useCallback(() => load(`view-diet-out/${ip_id}/`, setDietOut), [load, ip_id]);
    const loadLab = useCallback(() => load(`view-ip-lab/${ip_id}`, setLabView), [load, ip_id]);
    const loadInv = useCallback(() => load(`view-ip-inv/${ip_id}`, setInvView), [load, ip_id]);
    const loadFiles = useCallback(() => load(`view-files/${ip_id}`, setMedView), [load, ip_id]);

    useEffect(() => {
        if (!ip_id) return;
        load(`edit-ip-admit/${ip_id}/`, (d) => setIpData([d]));
        load("cons-search/", setConsData);
        load("indent-pharm-search/?dept=Pharmacy", setDrugItems);
        load("lab-search/", setLabItems);
        load("inv-search/", setInvItems);
        load("min-search/", setMinItems);
        load(`view-use-drug-ip/${ip_id}/`, setCdrugData);
        load(`view-vital/${ip_id}/`, setVitalData);
        loadCon(); loadCnotes(); loadNurse(); loadDrug(); loadInsulin();
        loadDietIn(); loadDietOut(); loadLab(); loadInv(); loadFiles();
    }, [ip_id, load, loadCon, loadCnotes, loadNurse, loadDrug, loadInsulin, loadDietIn, loadDietOut, loadLab, loadInv, loadFiles]);

    /* case sheet: fill the form ONCE per saved record, never overwrite while typing */
    useEffect(() => {
        if (!conData.length) return;
        const rec = conData[0];
        const key = String(rec.id);
        if (hydratedRef.current === key) return;
        hydratedRef.current = key;
        const next = emptyCase();
        CASE_KEYS.forEach((k) => {
            if (rec[k] !== null && rec[k] !== undefined) next[k] = String(rec[k]);
        });
        setForm(next);
    }, [conData]);

    /* consultant notes: default to the primary consultant */
    useEffect(() => {
        if (consSel || !consultId || !consData.length) return;
        if (consData.some((c) => String(c.id) === String(consultId))) setConsSel(String(consultId));
    }, [consSel, consultId, consData]);

    /* names of medicines that are not in the live stock list */
    useEffect(() => {
        if (!drugitems.length) return;
        const ids = new Set();
        drugData.forEach((r) => splitCsv(r.drug_id).filter(Boolean).forEach((id) => ids.add(id)));
        ids.forEach((id) => {
            if (drugNames[id] || triedNamesRef.current.has(id)) return;
            if (drugitems.some((it) => String(it.id) === id)) return;
            triedNamesRef.current.add(id);
            axios.get(`${baseUrl}update-indent-use/${id}/`)
                .then((r) => setDrugNames((p) => ({...p, [id]: r.data?.product_name || ""})))
                .catch(console.log);
        });
    }, [drugData, drugitems, drugNames]);

    const nameForId = (id) => {
        const it = drugitems.find((x) => String(x.id) === String(id));
        return it?.product?.product || drugNames[String(id)] || "";
    };

    /* =================================================================
       VOICE  (keys are namespaced: "cs:<field>" case sheet, "cn:<field>" consultant notes)
       ================================================================= */

    const {transcript, resetTranscript, browserSupportsSpeechRecognition} = useSpeechRecognition({continuous: true});
    const [activeField, setActiveField] = useState(null);

    const getVal = (k) => (k.startsWith("cn:") ? consNotes[k.slice(3)] ?? "" : form[k.slice(3)] ?? "");
    const setVal = (k, v) => (k.startsWith("cn:")
        ? setConsNotes((p) => ({...p, [k.slice(3)]: v}))
        : setForm((p) => ({...p, [k.slice(3)]: v})));

    useEffect(() => {
        if (!activeField) return;
        setVal(activeField, joinText(baseRef.current, transcript));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcript, activeField]);
    useEffect(() => () => SpeechRecognition.stopListening(), []);

    const startListening = (key) => {
        baseRef.current = getVal(key);
        resetTranscript();
        setActiveField(key);
        SpeechRecognition.startListening({continuous: true, interimResults: true, language: "en-IN"});
    };
    const stopListening = () => {
        SpeechRecognition.stopListening();
        setActiveField(null);
    };
    const onNoteChange = (key, value) => {
        if (key === activeField) {
            baseRef.current = value;
            resetTranscript();
        }
        setVal(key, value);
    };
    const noteProps = (key) => ({
        name: key, value: getVal(key), onChange: onNoteChange, active: activeField === key,
        onStart: startListening, onStop: stopListening, supported: browserSupportsSpeechRecognition,
    });

    /* =================================================================
       CASE SHEET
       ================================================================= */

    const inputHandler = (e) => {
        const {name, value} = e.target;
        setForm((prev) => {
            const u = {...prev, [name]: value};
            if (name === "weight" || name === "height") {
                const w = parseFloat(u.weight), h = parseFloat(u.height);
                u.bmi = !isNaN(w) && !isNaN(h) && h > 0 ? (w / ((h / 100) ** 2)).toFixed(2) : "";
            }
            return u;
        });
    };

    const searchIcd = (kind) => (e, text) => {
        if (!text || text.length < 2) return;
        const url = kind === "dis" ? "icd-dis-search" : "icd-code-search";
        axios.get(`${baseUrl}${url}/?search=${encodeURIComponent(text)}`)
            .then((r) => (kind === "dis"
                ? setIcdDis((r.data.results || []).map((x) => ({value: x.disease_code, label: `${x.disease_code} - ${x.disease_desc}`})))
                : setIcdCode((r.data.results || []).map((x) => ({value: x.sub_category, label: `${x.sub_category} - ${x.title}`})))))
            .catch((err) => console.error("ICD search failed:", err));
    };

    const saveCase = async (e) => {
        e?.preventDefault();
        const rec = conData[0];
        if (!rec?.id || saving) return;
        const fd = new FormData();
        fd.append("ip", ip_id);
        CASE_KEYS.forEach((k) => fd.append(k, form[k] ?? ""));
        if (icd.icd) fd.append("icd_disease", icd.icd);
        if (icd.code) fd.append("icd_code", icd.code);
        fd.append("ip_add", ipAddress);
        fd.append("location", userLocation);
        setSaving(true);
        try {
            const res = await axios.put(`${baseUrl}edit-ip-consult-form/${rec.id}/`, fd);
            if (res.status === 200 || res.status === 201) {
                toast("Details Updated.");
                setIcd({icd: "", code: ""});
                hydratedRef.current = null;
                await loadCon();
            }
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Failed to update", "error");
        } finally {
            setSaving(false);
        }
    };

    /* =================================================================
       DRUG CHART  (the old separate "IP Drug Entry" page lives here now)
       ================================================================= */

    const updateDrug = (i, name, value) => setDrugRows((p) => p.map((r, x) => (x === i ? {...r, [name]: value} : r)));
    const selectDrug = (i, val) => {
        setDrugRows((p) => p.map((r, x) => (x === i ? {...r, id: val ? val.id : "", qty: val ? r.qty : ""} : r)));
        if (val) {
            const key = drugRows[i]?.key;
            setTimeout(() => document.querySelector(`[data-qty-key="${key}"]`)?.focus(), 0);
        }
    };
    const addDrugRow = () => {
        const r = blankDrug();
        focusRef.current = r.key;
        setDrugRows((p) => [...p, r]);
    };
    const removeDrugRow = (i) => setDrugRows((p) => (p.length > 1 ? p.filter((_, x) => x !== i) : [blankDrug()]));

    useEffect(() => {
        if (!focusRef.current) return;
        document.querySelector(`[data-drug-key="${focusRef.current}"]`)?.focus();
        focusRef.current = null;
    }, [drugRows]);

    const onDrugKeyDown = (e, i) => {
        if (e.key === "Shift" && !e.repeat && i === drugRows.length - 1 && drugRows[i].id) {
            e.preventDefault();
            addDrugRow();
        } else if (e.key === "Alt" && drugRows.length > 1) {
            e.preventDefault();
            removeDrugRow(i);
        }
    };

    const drugFilter = (options, state) => {
        const q = state.inputValue.toLowerCase().trim();
        if (q.length < 3) return [];
        return options.filter((o) => (o.label || o.product?.product || "").toLowerCase().startsWith(q)
            || (o.product?.generic || "").toLowerCase().startsWith(q));
    };

    const submitDrugs = async (e) => {
        e?.preventDefault();
        if (savingDrug) return;
        if (!patId) {
            Swal.fire("Patient not loaded", "Please wait for the patient details to load and try again.", "warning");
            return;
        }
        const chosen = drugRows.filter((r) => r.id);
        if (!chosen.length) {
            Swal.fire("Select a medicine", "Please select at least one medicine.", "warning");
            return;
        }
        if (chosen.some((r) => !(parseFloat(r.qty) > 0))) {
            Swal.fire("Quantity missing", "Enter a quantity greater than 0 for every selected medicine.", "warning");
            return;
        }
        const fd = new FormData();
        fd.append("ip", ip_id);
        fd.append("pat", patId);
        fd.append("drug_id", chosen.map((r) => r.id).join(","));
        fd.append("qty", chosen.map((r) => r.qty).join(","));
        fd.append("drug_status", "1");
        fd.append("addedby", userName);
        fd.append("ip_add", ipAddress);
        fd.append("location", userLocation);
        setSavingDrug(true);
        try {
            const res = await axios.post(`${baseUrl}ip-drug-create/`, fd);
            if (res.status === 200 || res.status === 201) {
                toast("Medicine request sent to pharmacy.");
                setDrugRows([blankDrug()]);
                loadDrug();
            }
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Failed. Please try again later.", "error");
        } finally {
            setSavingDrug(false);
        }
    };

    /* =================================================================
       CONSULTANT NOTES
       ================================================================= */

    const consRecord = consData.find((c) => String(c.id) === String(consSel));
    const consDept = String(consRecord?.department || "").replace(/\s+/g, "_").toUpperCase();
    const consFields = DEPARTMENT_FIELDS[consDept] || [];

    const submitConsNote = async (e) => {
        e?.preventDefault();
        if (savingCons) return;
        if (!consSel) {
            Swal.fire("Select consultant", "Please choose the consultant for these notes.", "warning");
            return;
        }
        const notes = {};
        consFields.forEach((f) => {
            notes[f.name] = String(consNotes[f.name] ?? "").trim();
        });
        if (!Object.values(notes).some(Boolean)) {
            Swal.fire("Nothing to save", "Please enter at least one note.", "warning");
            return;
        }
        stopListening();
        const fd = new FormData();
        fd.append("ip", ip_id);
        fd.append("cons", consSel);
        fd.append("notes", JSON.stringify(notes));
        fd.append("notes_time", formatNoteTime(consTime));
        fd.append("status", "1");
        fd.append("addedby", userName);
        fd.append("ip_add", ipAddress);
        fd.append("location", userLocation);
        setSavingCons(true);
        try {
            const res = await axios.post(`${baseUrl}consult-note-create/`, fd);
            if (res.status === 200 || res.status === 201) {
                toast("Notes Created Succesfully");
                setConsNotes({});
                setConsTime(new Date());
                loadCnotes();
            }
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Failed. Please try again later.", "error");
        } finally {
            setSavingCons(false);
        }
    };

    /* one delete helper for every history table */
    const deleteRecord = async (url, refetch) => {
        if (!(await confirmBox())) return;
        try {
            await axios.delete(url);
            toast("Data has been Deleted");
            refetch();
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Data has not been deleted", "error");
        }
    };

    /* =================================================================
       LAB / RADIOLOGY / MIN
       ================================================================= */

    const submitOrder = async (kind) => {
        const cfg = ORDER_CFG[kind], st = orders[kind];
        const chosen = st.rows.filter((r) => String(r.id || "").trim() !== "");
        if (!chosen.length) {
            Swal.fire(cfg.emptyTitle, cfg.emptyText, "warning");
            return;
        }
        if (!patId || !consultId) {
            Swal.fire("Admission not loaded", "Please wait for the admission details to load and try again.", "warning");
            return;
        }
        setSubmittingOrder(kind);
        try {
            const num = await axios.post(`${baseUrl}${cfg.numberUrl}`);
            const fd = new FormData();
            fd.append("appt_id", num.data.value);
            fd.append("ip", ip_id);
            fd.append("pat", patId);
            fd.append("cons", consultId);
            if (cfg.linkField) fd.append(cfg.linkField, num.data.value);
            fd.append(cfg.ids, chosen.map((r) => r.id).join(","));
            fd.append(cfg.names, chosen.map((r) => r.name).join(","));
            if (cfg.unitsField) fd.append(cfg.unitsField, chosen.map((r) => Math.max(parseFloat(r.units) || 1, 1)).join(","));
            fd.append(cfg.amounts, chosen.map((r) => r.amount).join(","));
            fd.append(cfg.tot, orderTotal(st, !!cfg.unitsField));
            fd.append(cfg.disc, "0");
            fd.append(cfg.status, "1");
            fd.append("addedby", userName);
            fd.append("ip_add", ipAddress);
            fd.append("location", userLocation);
            const res = await axios.post(`${baseUrl}${cfg.createUrl}`, fd);
            if (res.status === 200 || res.status === 201) {
                toast(cfg.done);
                st.reset();
                if (kind === "lab") loadLab();
                if (kind === "inv") loadInv();
            }
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Failed to add entry. Please try again later.", "error");
        } finally {
            setSubmittingOrder("");
        }
    };

    /* =================================================================
       MEDICAL FILES
       ================================================================= */

    const submitFile = async (e) => {
        e.preventDefault();
        if (!medFile.file) {
            Swal.fire("Select a file", "Please choose a file to upload.", "warning");
            return;
        }
        const fd = new FormData();
        fd.append("appt_id", ip_id);
        fd.append("file_type", medFile.ftype);
        fd.append("files", medFile.file, medFile.file.name);
        fd.append("comments", medFile.comments);
        fd.append("file_date", formatLocalDate(medicalDate));
        fd.append("status", "1");
        fd.append("addedby", userName);
        fd.append("ip_add", ipAddress);
        fd.append("location", userLocation);
        try {
            const res = await axios.post(`${baseUrl}op-file-create/`, fd);
            if (res.status === 200 || res.status === 201) {
                toast("File has been added.");
                setMedFile({ftype: "ctscan", comments: "", file: null});
                const input = document.getElementById("f_files");
                if (input) input.value = "";
                loadFiles();
            }
        } catch (err) {
            console.log(err);
            Swal.fire("Error!", "Failed to upload file. Please try again later.", "error");
        }
    };

    /* =================================================================
       VITALS CHART
       ================================================================= */

    useEffect(() => {
        if (tab !== "vitals" || !chartRef.current || !vitalData.length) return;
        const line = (label, key, color) => ({
            label, data: vitalData.map((v) => v[key]), borderColor: color, backgroundColor: color, tension: 0.3, pointRadius: 4,
        });
        const chart = new Chart(chartRef.current, {
            type: "line",
            data: {
                labels: vitalData.map((v) => v.vital_time),
                datasets: [line("Temperature", "tempr", "#ef4444"), line("Pulse", "pulse", "#2563eb"), line("Respiration", "resp", "#f59e0b")],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {mode: "index", intersect: false},
                scales: {x: {title: {display: true, text: "Time"}}, y: {title: {display: true, text: "Value"}}},
            },
        });
        return () => chart.destroy();
    }, [tab, vitalData]);

    /* =================================================================
       RENDER
       ================================================================= */

    const symptomList = form.symptom && !SYMPTOMS.includes(form.symptom) ? [...SYMPTOMS, form.symptom] : SYMPTOMS;
    const savedRec = conData[0];
    const tabCount = {nurse: notesData.length, cons: cnotesData.length, drug: drugData.length, lab: labview.length, inv: invview.length, med: medview.length};

    /* given-drug history: rows of "view-use-drug-ip" plus any request rows that already carry a given time */
    const givenRows = [
        ...(Array.isArray(cdrugData) ? cdrugData.map((r) => ({...r, _k: `u${r.id}`})) : []),
        ...(Array.isArray(drugData) ? drugData.filter((r) => r.drug_time).map((r) => ({...r, _k: `d${r.id}`})) : []),
    ].filter((r) => r.drug_time);

    return (
        <section className="container cd-page">

            {/* ---------- header ---------- */}
            <div className="cd-hero">
                <div className="cd-hero-title">
                    <span className="cd-hero-icon"><i className="fa-solid fa-bed-pulse"/></span>
                    <div>
                        <small>In-Patient · Primary Consultant</small>
                        <h4>Dr. {ip?.primary_consult?.consultant}<span> · IP {ip?.ip_id}</span></h4>
                    </div>
                </div>
                <div className="cd-hero-actions">
                    <Link to={`/ip-pat-print/${ip_id}`} className="btn btn-warning"><i className="fa-solid fa-id-card me-2"/>IP Admit Card</Link>
                </div>
            </div>

            {/* ---------- patient strip ---------- */}
            {ip?.pat && (
                <div className="cd-patient">
                    {ip.profile_pic && (
                        <div className="cd-patient-item"><label>Patient</label>
                            <img src={ip.profile_pic} alt="Patient" style={{width: 80, height: 48, objectFit: "cover", borderRadius: 8}}/>
                        </div>
                    )}
                    {[["IP ID", ip.ip_id], ["Patient ID", ip.pat.pat_id], ["Name", ip.pat.patient], ["Gender", ip.pat.gender], ["D.O.B/Age", ip.pat.age],
                        ["Mobile", ip.pat.mobile], ["Admitted On", ip.admit_date ? formatDateTime(ip.admit_date) : "-"], ["Pat Type", ip.condition]].map(([l, v]) => (
                        <div key={l} className="cd-patient-item"><label>{l}</label><span>{v || "-"}</span></div>
                    ))}
                </div>
            )}

            {/* ---------- tabs ---------- */}
            <nav className="cd-tabs">
                {TABS.map((t) => (
                    <button key={t.id} type="button" className={`cd-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                        <i className={`fa-solid ${t.icon}`}/>{t.label}
                        {tabCount[t.id] > 0 && <em>{tabCount[t.id]}</em>}
                    </button>
                ))}
            </nav>

            <div className="cd-panel">

                {/* ================= CASE SHEET ================= */}
                {tab === "case" && (
                    <form onSubmit={saveCase} noValidate>
                        {conData.length === 0 && (
                            <div className="alert alert-warning">
                                <i className="fa-solid fa-circle-info me-2"/>No case sheet record exists for this admission yet, so there is nothing to update.
                            </div>
                        )}

                        <SectionTitle icon="fa-heart-pulse">Vitals & Symptoms</SectionTitle>
                        <div className="row">
                            {[["weight", "Weight (Kg)", "fa-weight-scale"], ["height", "Height (cm)", "fa-ruler-vertical"], ["bmi", "BMI", "fa-calculator", true]].map(([name, label, icon, ro]) => (
                                <div key={name} className="col-lg-3 col-md-6 mb-3">
                                    <label className="form-label">{label}</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><i className={`fa-solid ${icon}`}/></span>
                                        <input type="number" className="form-control" placeholder={label} name={name} value={form[name]} onChange={inputHandler} readOnly={!!ro}/>
                                    </div>
                                </div>
                            ))}
                            <div className="col-lg-3 col-md-6 mb-3">
                                <label className="form-label">Symptoms</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-stethoscope"/></span>
                                    <select className="form-select" name="symptom" value={form.symptom} onChange={inputHandler}>
                                        <option value="">--Select--</option>
                                        {symptomList.map((s) => <option key={s} value={s}>{s.trim()}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <SectionTitle icon="fa-file-waveform">Clinical Notes</SectionTitle>
                        <div className="row">
                            {CASE_NOTES.map(([name, label]) => <NoteField key={name} label={label} rows={5} {...noteProps(`cs:${name}`)}/>)}
                        </div>

                        <SectionTitle icon="fa-disease">ICD Coding</SectionTitle>
                        <div className="row">
                            {[
                                ["icd", "ICD Disease Code", "icd_disease", icdDis, "dis", "Type 2+ letters or code"],
                                ["code", "ICD Procedure Code", "icd_code", icdCode, "code", "Type 2+ letters or code"],
                            ].map(([stateKey, label, prevKey, options, kind, ph]) => (
                                <div key={stateKey} className="col-lg-6 mb-4">
                                    <label className="form-label">{label}</label>
                                    {savedRec?.[prevKey] && (
                                        <div className="alert alert-success py-2 mb-2">
                                            <i className="fa-solid fa-clock-rotate-left me-2"/><strong>Previous:</strong> {savedRec[prevKey]}
                                        </div>
                                    )}
                                    <AutoComplete
                                        options={options}
                                        getOptionLabel={(o) => o?.label || ""}
                                        isOptionEqualToValue={(o, v) => o.value === v.value}
                                        filterOptions={(x) => x}
                                        onInputChange={searchIcd(kind)}
                                        onChange={(e, val) => setIcd((p) => ({...p, [stateKey]: val ? val.value : ""}))}
                                        renderInput={(params) => (
                                            <div ref={params.InputProps.ref}>
                                                <input className="form-control" type="text" placeholder={ph} {...params.inputProps}/>
                                            </div>
                                        )}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="cd-savebar">
                            <button type="submit" className="btn btn-success" disabled={saving || !savedRec}>
                                <i className="fa-solid fa-floppy-disk me-2"/>{saving ? "Saving..." : "Save Case Sheet"}
                            </button>
                        </div>
                    </form>
                )}

                {/* ================= NURSE NOTES ================= */}
                {tab === "nurse" && (
                    <>
                        <SectionTitle icon="fa-user-nurse">Nurse Notes History</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th style={{width: 200}}>Date</th><th>Notes</th>{NURSE_NOTE_DELETE_URL && <th style={{width: 100}}>Delete</th>}</tr></thead>
                                <tbody>
                                {notesData.map((n) => (
                                    <tr key={n.id}>
                                        <td>{formatDateTime(n.notes_time)}</td>
                        <td style={{
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    minWidth: '200px',
    maxWidth: '400px'
}}>
    {n.notes || '-'}
</td>
                                        {NURSE_NOTE_DELETE_URL && (
                                            <td><button className="btn btn-outline-danger" type="button" onClick={() => deleteRecord(NURSE_NOTE_DELETE_URL(n.id), loadNurse)}><i className="fa-solid fa-trash-can"/></button></td>
                                        )}
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {notesData.length === 0 && <Empty>No nurse notes yet.</Empty>}
                        </div>
                    </>
                )}

                {/* ================= CONSULTANT NOTES ================= */}
                {tab === "cons" && (
                    <>
                        <SectionTitle icon="fa-user-doctor">Consultant Notes</SectionTitle>
                        <form onSubmit={submitConsNote} noValidate>
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Consultant</label>
                                    <select className="form-select" value={consSel} onChange={(e) => setConsSel(e.target.value)}>
                                        <option value="">--SELECT CONSULTANT--</option>
                                        {consData.map((c) => <option key={c.id} value={c.id}>{c.consultant} - {c.department}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Notes Time</label>
                                    <DatePicker className="form-control" selected={consTime} onChange={(d) => d && setConsTime(d)}
                                                dateFormat="yyyy-MM-dd HH:mm" showTimeSelect timeIntervals={15} timeCaption="Time" timeFormat="HH:mm"/>
                                </div>
                            </div>

                            <SectionTitle icon="fa-file-pen">Casesheet {consRecord && <small>· {consRecord.department}</small>}</SectionTitle>
                            <div className="row">
                                {consFields.map((f) => <NoteField key={f.name} label={f.label} rows={4} col="col-xl-3 col-lg-4 col-md-6" {...noteProps(`cn:${f.name}`)}/>)}
                            </div>
                            {consSel && consFields.length === 0 && <Empty>No note template for this department.</Empty>}

                            <div className="cd-savebar">
                                <button type="submit" className="btn btn-primary" disabled={savingCons || consFields.length === 0}>
                                    <i className="fa-solid fa-paper-plane me-2"/>{savingCons ? "Saving..." : "Submit"}
                                </button>
                            </div>
                        </form>

                        <SectionTitle icon="fa-clock-rotate-left">Consultant Notes History</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th style={{width: 180}}>Date</th><th style={{width: 200}}>Consultant</th><th>Notes</th><th style={{width: 100}}>Delete</th></tr></thead>
                                <tbody>
                                {cnotesData.map((n) => (
                                    <tr key={n.id}>
                                        <td>{formatDateTime(n.notes_time)}</td>
                                        <td>{n.cons?.consultant}</td>
                                        <td>
                                            {n.notes && (() => {
                                                try {
                                                    return Object.entries(JSON.parse(n.notes)).filter(([, v]) => v).map(([k, v]) => (
                                                        <div key={k}>{k.replace(/_/g, " ").toUpperCase()}: <strong>{v}</strong></div>
                                                    ));
                                                } catch {
                                                    return n.notes;
                                                }
                                            })()}
                                        </td>
                                        <td><button className="btn btn-outline-danger" type="button" onClick={() => deleteRecord(`${baseUrl}delete-consult-note/${n.id}/`, loadCnotes)}><i className="fa-solid fa-trash-can"/></button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {cnotesData.length === 0 && <Empty>No consultant notes yet.</Empty>}
                        </div>
                    </>
                )}

                {/* ================= DRUG CHART ================= */}
                {tab === "drug" && (
                    <>
                        <SectionTitle icon="fa-pills" right={<small className="text-muted">Shift = add row · Alt = delete row</small>}>Medicine Request</SectionTitle>
                        <form onSubmit={submitDrugs} noValidate>
                            <div className="table-responsive">
                                <table className="erp-table-modern cd-table">
                                    <thead>
                                    <tr><th style={{width: 80}}>ID</th><th style={{minWidth: 300}}>Drug name</th><th style={{width: 140}}>QTY</th><th style={{width: 90}}>Action</th></tr>
                                    </thead>
                                    <tbody>
                                    {drugRows.map((row, i) => (
                                        <tr key={row.key}>
                                            <td><input type="text" className="form-control" value={row.id} readOnly tabIndex={-1}/></td>
                                            <td>
                                                <AutoComplete
                                                    options={drugitems}
                                                    value={drugitems.find((it) => String(it.id) === String(row.id)) || null}
                                                    isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                                                    getOptionLabel={(o) => o?.product?.product || ""}
                                                    filterOptions={drugFilter}
                                                    onChange={(e, val) => selectDrug(i, val)}
                                                    onKeyDown={(e) => onDrugKeyDown(e, i)}
                                                    renderOption={(props, option) => {
                                                        const {key, ...rest} = props;
                                                        return (
                                                            <li key={option.id ?? key} {...rest}>
                                                                <div style={{width: "100%"}}>
                                                                    <div style={{fontWeight: 600}}>{option.product?.product || ""}</div>
                                                                    <div style={{fontSize: 12, color: "#777"}}>
                                                                        ID: {option.product?.id ?? "-"} | Batch: {option.product?.batch || "-"} | Expy: {option.product?.expiry || "-"} | 1s MRP: {option.product?.uamount ?? "-"} | TQty: {option.qty ?? 0}
                                                                    </div>
                                                                </div>
                                                            </li>
                                                        );
                                                    }}
                                                    renderInput={(params) => (
                                                        <div ref={params.InputProps.ref}>
                                                            <input {...params.inputProps} data-drug-key={row.key} className="form-control cd-ac-input" type="text" placeholder="Type 3+ letters"/>
                                                        </div>
                                                    )}
                                                />
                                            </td>
                                            <td>
                                                <input type="number" min="0" className="form-control" data-qty-key={row.key} value={row.qty}
                                                       onKeyDown={(e) => onDrugKeyDown(e, i)}
                                                       onChange={(e) => (e.target.value === "" || parseFloat(e.target.value) >= 0) && updateDrug(i, "qty", e.target.value)}/>
                                            </td>
                                            <td><button className="btn btn-outline-danger" type="button" onClick={() => removeDrugRow(i)}><i className="fa-solid fa-trash-can"/></button></td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="cd-order-foot">
                                <button className="btn btn-outline-success" type="button" onClick={addDrugRow}><i className="fa-solid fa-plus me-2"/>Add More</button>
                                <button type="submit" className="btn btn-primary ms-auto" disabled={savingDrug}>
                                    <i className="fa-solid fa-paper-plane me-2"/>{savingDrug ? "Sending..." : "Submit"}
                                </button>
                            </div>
                        </form>

                        <SectionTitle icon="fa-prescription-bottle-medical">Medicine Request View</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th>Date/Time</th><th>Drug ID</th><th>Drug Name</th><th>Qty</th><th>Addedby</th><th>Status</th></tr></thead>
                                <tbody>
                                {Array.isArray(drugData) && drugData.map((row) => {
                                    const ids = splitCsv(row.drug_id), qtys = splitCsv(row.qty);
                                    return ids.map((id, i) => !id ? null : (
                                        <tr key={`${row.id}-${id}-${i}`}>
                                            <td>{row.add_date}/{String(row.add_time).split(".")[0]}</td>
                                            <td>{id}</td>
                                            <td>{nameForId(id) || "Drug not found"}</td>
                                            <td>{qtys[i] || "-"}</td>
                                            <td>{row.addedby}</td>
                                            <td>{String(row.drug_status) === "1"
                                                ? <span className="badge bg-warning">Sent To Pharmacy</span>
                                                : <span className="badge bg-secondary">Status {row.drug_status ?? "?"}</span>}</td>
                                        </tr>
                                    ));
                                })}
                                </tbody>
                            </table>
                            {(!Array.isArray(drugData) || drugData.length === 0) && <Empty>No medicine requests yet.</Empty>}
                        </div>

                        <SectionTitle icon="fa-clock-rotate-left">Drug Chart History</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th>Given Time</th><th>Date/Time</th><th>Drug ID</th><th>Drug Name</th><th>Qty</th><th>Addedby</th></tr></thead>
                                <tbody>
                                {givenRows.map((r) => (
                                    <tr key={r._k}>
                                        <td>{r.add_time?.split(".")[0]}</td>
                                        <td>{r.drug_time}</td>
                                        <td>{r.drug?.id ?? r.drug_id}</td>
                                        <td>{r.drug?.product?.product || nameForId(r.drug_id) || "-"}</td>
                                        <td>{r.qty}</td>
                                        <td>{r.addedby}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {givenRows.length === 0 && <Empty>No drugs have been given yet.</Empty>}
                        </div>
                    </>
                )}

                {/* ================= INSULIN ================= */}
                {tab === "insulin" && (
                    <>
                        <SectionTitle icon="fa-syringe">Insulin History</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th>Date</th><th colSpan="6">Insulin Details</th>{INSULIN_DELETE_URL && <th>Delete</th>}</tr></thead>
                                <tbody>
                                {insulinData.map((d) => (
                                    <tr key={d.id}>
                                        <td>{formatDateTime(d.insulin_time)}</td>
                                        <td>Fluid : {d.fluid}</td>
                                        <td>Drug - Units: {d.drug} - {d.units}</td>
                                        <td>Drops: {d.drops}</td>
                                        <td>INS : {d.ins}</td>
                                        <td>Blood Sugar : {d.bls}</td>
                                        <td>Sugar Type : {d.blstype}</td>
                                        {INSULIN_DELETE_URL && (
                                            <td><button className="btn btn-outline-danger" type="button" onClick={() => deleteRecord(INSULIN_DELETE_URL(d.id), loadInsulin)}><i className="fa-solid fa-trash-can"/></button></td>
                                        )}
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {insulinData.length === 0 && <Empty>No insulin entries yet.</Empty>}
                        </div>
                    </>
                )}

                {/* ================= DIET ================= */}
                {tab === "diet" && (
                    <>
                        <SectionTitle icon="fa-apple-whole">Diet Intake History</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th>Date</th><th colSpan="7">Intake Details</th><th>Notes</th><th>Delete</th></tr></thead>
                                <tbody>
                                {dietIn.map((d) => (
                                    <tr key={d.id}>
                                        <td>{formatDateTime(d.in_time)}</td>
                                        <td>{d.blood}</td><td>{d.bqty}</td><td>{d.fluid}</td><td>{d.fqty}</td><td>{d.ngqty}</td><td>{d.oral}</td><td>{d.oqty}</td>
                                        <td>{d.in_notes}</td>
                                        <td><button className="btn btn-outline-danger" type="button" onClick={() => deleteRecord(`${baseUrl}delete-diet-in/${d.id}/`, loadDietIn)}><i className="fa-solid fa-trash-can"/></button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {dietIn.length === 0 && <Empty>No intake entries yet.</Empty>}
                        </div>

                        <SectionTitle icon="fa-droplet">Diet Out History</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th>Date</th><th colSpan="5">Output Details</th><th>Notes</th><th>Delete</th></tr></thead>
                                <tbody>
                                {dietOut.map((d) => (
                                    <tr key={d.id}>
                                        <td>{formatDateTime(d.out_time)}</td>
                                        <td>{d.urine}</td><td>{d.uqty}</td><td>{d.motion}</td><td>{d.mqty}</td><td>{d.ngqty}</td>
                                        <td>{d.out_notes}</td>
                                        <td><button className="btn btn-outline-danger" type="button" onClick={() => deleteRecord(`${baseUrl}delete-diet-out/${d.id}/`, loadDietOut)}><i className="fa-solid fa-trash-can"/></button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {dietOut.length === 0 && <Empty>No output entries yet.</Empty>}
                        </div>
                    </>
                )}

                {/* ================= LAB / RADIOLOGY / MIN ================= */}
                {tab === "lab" && (
                    <OrderTab title="Lab Request" nameHeader="Lab Test Name" items={labitems} labelKey="subcat" state={lab}
                              onSubmit={() => submitOrder("lab")} submitting={submittingOrder === "lab"}
                              viewTitle="Lab Request View" viewRows={labview} viewNameKey="lab_test" badge={labBadge}/>
                )}
                {tab === "inv" && (
                    <OrderTab title="Radiology Request" nameHeader="Inv Test Name" items={invitems} labelKey="components" state={inv}
                              onSubmit={() => submitOrder("inv")} submitting={submittingOrder === "inv"}
                              viewTitle="Radiology Request View" viewRows={invview} viewNameKey="inv_test" badge={invBadge}/>
                )}
                {tab === "min" && (
                    <OrderTab title="Minor Treatment" nameHeader="Min. Test Name" items={minitems} labelKey="comp" state={min} units
                              onSubmit={() => submitOrder("min")} submitting={submittingOrder === "min"}/>
                )}

                {/* ================= MEDICAL SUMMARY ================= */}
                {tab === "med" && (
                    <>
                        <SectionTitle icon="fa-cloud-arrow-up">Medical File Upload</SectionTitle>
                        <form onSubmit={submitFile} className="cd-upload">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label">Type of File</label>
                                    <select className="form-select" value={medFile.ftype} onChange={(e) => setMedFile((p) => ({...p, ftype: e.target.value}))}>
                                        <option value="ctscan">CT-Scan</option>
                                        <option value="xray">X-Ray</option>
                                        <option value="casesheet">Case Sheet</option>
                                        <option value="other">Other Docs</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">File Created Date</label>
                                    <DatePicker selected={medicalDate} onChange={(d) => d && setMedicalDate(d)} dateFormat="yyyy-MM-dd" className="form-control"/>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">File Upload</label>
                                    <input type="file" className="form-control" id="f_files" onChange={(e) => setMedFile((p) => ({...p, file: e.target.files[0] || null}))}/>
                                </div>
                                <div className="col-12">
                                    <label className="form-label">Comments</label>
                                    <textarea className="form-control no-comma" rows={3} value={medFile.comments} onChange={(e) => setMedFile((p) => ({...p, comments: e.target.value}))}/>
                                </div>
                            </div>
                            <button type="submit" className="btn btn-primary mt-3"><i className="fa-solid fa-upload me-2"/>Submit</button>
                        </form>

                        <SectionTitle icon="fa-folder-open">Medical Document View</SectionTitle>
                        <div className="table-responsive">
                            <table className="erp-table-modern cd-table">
                                <thead><tr><th>Appt ID</th><th>File Type</th><th>File Date</th><th>Comments</th><th>View</th><th>Addedby</th><th>Action</th></tr></thead>
                                <tbody>
                                {medview.map((m) => (
                                    <tr key={m.id}>
                                        <td>{m.appt?.appt_id}</td><td>{m.file_type}</td><td>{m.file_date}</td><td>{m.comments}</td>
                                        <td><iframe src={m.files} title={String(m.files)} allowFullScreen/></td>
                                        <td>{m.addedby}</td>
                                        <td><button onClick={() => deleteRecord(`${baseUrl}delete-files/${m.id}`, loadFiles)} className="btn btn-outline-danger btn-sm"><i className="fa-solid fa-trash-can"/></button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {medview.length === 0 && <Empty>No documents uploaded yet.</Empty>}
                        </div>
                    </>
                )}

                {/* ================= VITALS ================= */}
                {tab === "vitals" && (
                    <>
                        <SectionTitle icon="fa-heart-pulse">Vitals Trend</SectionTitle>
                        {vitalData.length > 0
                            ? <div className="cd-canvas" style={{height: 460, padding: 12}}><canvas ref={chartRef}/></div>
                            : <Empty>No vitals recorded yet.</Empty>}
                    </>
                )}
            </div>
        </section>
    );
}

export default IpConsDetail;